import { useContext, useEffect, useState } from 'react';
import { PlaycastContext } from '../PlaycastWrapper';
import { Button, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@playcastdotio/Shadcn';

export const AfkPreventionControls = () => {
  const context = useContext(PlaycastContext);
  const { afkPreventionState } = context.stateStores;
  const settings = afkPreventionState;

  // Common key options for AFK prevention
  const keyOptions = [
    { value: 70, label: 'F', displayName: 'F' },
    { value: 87, label: 'W', displayName: 'W' },
    { value: 65, label: 'A', displayName: 'A' },
    { value: 83, label: 'S', displayName: 'S' },
    { value: 68, label: 'D', displayName: 'D' },
    { value: 32, label: 'Space', displayName: 'Space' },
    { value: 16, label: 'Shift', displayName: 'Shift' },
    { value: 17, label: 'Ctrl', displayName: 'Ctrl' },
    { value: 18, label: 'Alt', displayName: 'Alt' },
    { value: 13, label: 'Enter', displayName: 'Enter' },
    { value: 9, label: 'Tab', displayName: 'Tab' },
    { value: 27, label: 'Escape', displayName: 'Escape' },
    { value: 37, label: 'Left Arrow', displayName: 'Left Arrow' },
    { value: 38, label: 'Up Arrow', displayName: 'Up Arrow' },
    { value: 39, label: 'Right Arrow', displayName: 'Right Arrow' },
    { value: 40, label: 'Down Arrow', displayName: 'Down Arrow' },
    { value: 49, label: '1', displayName: '1' },
    { value: 50, label: '2', displayName: '2' },
    { value: 51, label: '3', displayName: '3' },
    { value: 52, label: '4', displayName: '4' },
    { value: 53, label: '5', displayName: '5' },
    { value: 96, label: 'Numpad 0', displayName: 'Numpad 0' },
    { value: 97, label: 'Numpad 1', displayName: 'Numpad 1' },
    { value: 98, label: 'Numpad 2', displayName: 'Numpad 2' },
    { value: 99, label: 'Numpad 3', displayName: 'Numpad 3' },
    { value: 100, label: 'Numpad 4', displayName: 'Numpad 4' },
    { value: 101, label: 'Numpad 5', displayName: 'Numpad 5' },
    { value: 112, label: 'F1', displayName: 'F1' },
    { value: 113, label: 'F2', displayName: 'F2' },
    { value: 114, label: 'F3', displayName: 'F3' },
    { value: 115, label: 'F4', displayName: 'F4' },
    { value: 116, label: 'F5', displayName: 'F5' },
    { value: 117, label: 'F6', displayName: 'F6' },
    { value: 118, label: 'F7', displayName: 'F7' },
    { value: 119, label: 'F8', displayName: 'F8' },
    { value: 120, label: 'F9', displayName: 'F9' },
    { value: 121, label: 'F10', displayName: 'F10' },
    { value: 122, label: 'F11', displayName: 'F11' },
    { value: 123, label: 'F12', displayName: 'F12' },
  ];

  const handleToggleEnabled = () => {
    const newEnabled = !settings.enabled;
    context.targetFunctions.setAfkPreventionEnabled(newEnabled);

    if (newEnabled) {
      context.targetFunctions.setAfkPreventionSettings({
        enabled: newEnabled,
        keyCode: settings.keyCode,
        intervalSeconds: settings.intervalSeconds,
        keyDisplayName: settings.keyDisplayName,
      });
    }
  };

  const handleKeyChange = (keyCode: string) => {
    const numericKeyCode = parseInt(keyCode, 10);
    const selectedKey = keyOptions.find((option) => option.value === numericKeyCode);
    const newSettings = {
      enabled: settings.enabled,
      keyCode: numericKeyCode,
      intervalSeconds: settings.intervalSeconds,
      keyDisplayName: selectedKey?.displayName || 'Unknown',
    };
    context.targetFunctions.setAfkPreventionSettings(newSettings);
  };

  const handleIntervalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const intervalSeconds = Math.max(1, Math.min(900, parseInt(e.target.value, 10) || 30));
    const newSettings = {
      enabled: settings.enabled,
      keyCode: settings.keyCode,
      intervalSeconds,
      keyDisplayName: settings.keyDisplayName,
    };
    context.targetFunctions.setAfkPreventionSettings(newSettings);
  };

  const handleTestKey = () => {
    context.targetFunctions.testAfkPreventionKey(settings.keyCode, settings.keyDisplayName);
  };

  return (
    <div className='flex flex-col space-y-4 rounded-lg border border-gray-700 p-4'>
      <h3 className='text-lg font-semibold'>AFK Prevention</h3>

      <div className='flex flex-col space-y-2'>
        <div className='flex items-center space-x-2'>
          <Button
            onClick={handleToggleEnabled}
            variant={settings.enabled ? 'default' : 'outline'}
            className={settings.enabled ? 'bg-green-600 hover:bg-green-700' : ''}
          >
            {settings.enabled ? 'Enabled' : 'Disabled'}
          </Button>
          <span className='text-sm text-gray-400'>{settings.enabled ? 'AFK prevention is active' : 'AFK prevention is inactive'}</span>
        </div>

        <div className='flex items-center space-x-2'>
          <label className='w-16 text-sm font-medium'>Key:</label>
          <Select value={settings.keyCode.toString()} onValueChange={handleKeyChange}>
            <SelectTrigger className='w-32'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {keyOptions.map((option) => (
                <SelectItem key={option.value} value={option.value.toString()}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant='outline' size='sm' onClick={handleTestKey}>
            Test Key
          </Button>
        </div>

        <div className='flex items-center space-x-2'>
          <label className='w-16 text-sm font-medium'>Interval:</label>
          <Input type='number' value={settings.intervalSeconds} onChange={handleIntervalChange} min='1' max='900' className='w-20' />
          <span className='text-sm text-gray-400'>seconds</span>
        </div>

        <div className='text-xs text-gray-500'>
          {settings.enabled && (
            <p>
              Pressing "{settings.keyDisplayName}" every {settings.intervalSeconds} seconds
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
